import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import { useCallback, useMemo, useState } from "react";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  DND_TOUCH_ACTIVATION,
  Separator,
  type IconName,
} from "@salestrak/ui";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import {
  sortByItemOrder,
  type CardDisplayOptions,
} from "#app/components/issue-card";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { CreateFieldPage, FieldsPicker } from "./add-field-page";
import {
  ItemRow,
  PinnedRow,
  SectionHeader,
  SortableItemRow,
  type DisplayItem,
} from "./display-field-rows";
import { useTrackViewOption } from "#app/lib/views/view-context";
import { useFieldsPageStep } from "./options-panel-slots";

// ---------------------------------------------------------------------------
// CardDisplayFields — the Fields page for issue-card display options
// ---------------------------------------------------------------------------
//
// These "fields" are the objects an issue card renders. The editor is the
// "Add existing" tab of the `FieldsPicker` the page opens on: a combined
// "Shown" group (drag to reorder — the order drives the card's body render
// order) and per-kind groups of hidden items. The card-wide layout
// modes live on the panel's main Display group (CardLayoutRows), not here —
// this page owns the per-item roster.
//
// Body only. The panel that hosts it owns the surface, the scroll and the
// header; this renders in flow.
// ---------------------------------------------------------------------------

// Footer FURNITURE (comments, assignee) has fixed seats and is deliberately
// absent: no show/hide, no reorder, not even a disabled row. Follow up is the
// one footer resident WITH a toggle (default on) — hideable, but its footer
// seat is fixed, so dragging its Shown row changes nothing on the card.
type PropertyKey =
  | "showAddress"
  | "showTeams"
  | "showStage"
  | "showWorkflow"
  | "showProjectType"
  | "showCreated"
  | "showUpdated"
  | "showAge";

const PROPERTY_META: {
  key: PropertyKey;
  /** The itemOrder / card body key. */
  orderKey: string;
  label: string;
  icon: IconName;
}[] = [
  { key: "showAddress", orderKey: "address", label: "Address", icon: PROPERTY_ICON.address },
  { key: "showTeams", orderKey: "teams", label: "Teams", icon: PROPERTY_ICON.team },
  { key: "showStage", orderKey: "stage", label: "Stage", icon: PROPERTY_ICON.stage },
  {
    key: "showWorkflow",
    orderKey: "workflow",
    label: "Workflow",
    icon: PROPERTY_ICON.workflow,
  },
  {
    key: "showProjectType",
    orderKey: "projectType",
    label: "Project Type",
    icon: PROPERTY_ICON.issueType,
  },
  { key: "showCreated", orderKey: "created", label: "Created", icon: PROPERTY_ICON.created },
  { key: "showUpdated", orderKey: "updated", label: "Updated", icon: PROPERTY_ICON.updated },
  { key: "showAge", orderKey: "age", label: "Follow up", icon: PROPERTY_ICON.followUp },
];

export type CardDisplayFieldsProps = {
  options: CardDisplayOptions;
  /** Patch-merge into the view's draft card options. */
  onChange: (patch: Partial<CardDisplayOptions>) => void;
  /** ALL workspace custom fields (unfiltered — hidden ones must stay listed). */
  fields: CustomFieldDef[];
};

/**
 * Properties + custom fields as display items. Custom fields are OPT-IN (an
 * inclusion list): shown only when picked. Shared by the page and by
 * `countShownCardFields`, so the main page's count and the Shown group can
 * never disagree.
 */
function cardDisplayItems(
  options: CardDisplayOptions,
  fields: CustomFieldDef[],
): DisplayItem[] {
  const shownFields = new Set(options.shownFieldIds ?? []);
  const props: DisplayItem[] = PROPERTY_META.map((p) => ({
    key: p.orderKey,
    label: p.label,
    icon: p.icon,
    kind: "property",
    shown: options[p.key],
  }));
  const flds: DisplayItem[] = fields.map((f) => {
    const meta = getFieldTypeMeta(f.type);
    return {
      key: f.customFieldId,
      label: f.name,
      icon: meta.icon,
      // Colored by field TYPE (the field-manager treatment) — properties
      // deliberately stay muted so user data reads distinct.
      iconColor: meta.color,
      kind: "field",
      shown: shownFields.has(f.customFieldId),
      field: f,
    };
  });
  return [...props, ...flds];
}

/** How many card items are shown, the pinned title aside — the main page's
 *  Fields row readout, from the same roster the page lists. */
export function countShownCardFields(
  options: CardDisplayOptions,
  fields: CustomFieldDef[],
): number {
  return cardDisplayItems(options, fields).filter((i) => i.shown).length;
}

export function CardDisplayFields({
  options,
  onChange,
  fields,
}: CardDisplayFieldsProps) {
  const [query, setQuery] = useState("");

  const allItems = useMemo(
    () => cardDisplayItems(options, fields),
    [options, fields],
  );

  const q = query.trim().toLowerCase();
  const matches = useCallback(
    (item: DisplayItem) => !q || item.label.toLowerCase().includes(q),
    [q],
  );

  const shownItems = useMemo(
    () =>
      sortByItemOrder(
        allItems.filter((i) => i.shown),
        (i) => i.key,
        options.itemOrder ?? [],
      ),
    [allItems, options.itemOrder],
  );
  const hiddenProps = allItems.filter((i) => !i.shown && i.kind === "property");
  const hiddenFields = allItems.filter((i) => !i.shown && i.kind === "field");

  const visibleShown = shownItems.filter(matches);
  const visibleHiddenProps = hiddenProps.filter(matches);
  const visibleHiddenFields = hiddenFields.filter(matches);
  const nameMatches = !q || "project name".includes(q);

  // Age is footer-seated: toggleable but NOT reorderable. It renders as a
  // fixed row at the END of Shown — mirroring the card (body items in
  // dragged order, then the footer resident) — outside the sortable list,
  // with no drag handle. The middle tier between the pinned-disabled
  // "Project Name" row and the fully sortable body rows.
  const sortableShown = visibleShown.filter((i) => i.key !== "age");
  const fixedShown = visibleShown.filter((i) => i.key === "age");

  // --- Toggle + reorder handlers ---
  const setFieldShown = useCallback(
    (customFieldId: string, shown: boolean) => {
      const next = new Set(options.shownFieldIds ?? []);
      if (shown) next.add(customFieldId);
      else next.delete(customFieldId);
      onChange({ shownFieldIds: Array.from(next) });
    },
    [onChange, options.shownFieldIds],
  );
  const trackOption = useTrackViewOption();
  const handleToggle = useCallback(
    (item: DisplayItem, shown: boolean) => {
      if (item.kind === "property") {
        const meta = PROPERTY_META.find((p) => p.orderKey === item.key);
        if (meta) {
          onChange({ [meta.key]: shown });
          trackOption(meta.key, { on: shown });
        }
        return;
      }
      setFieldShown(item.key, shown);
      trackOption("field", { fieldKind: item.kind, on: shown });
    },
    [onChange, setFieldShown, trackOption],
  );

  // --- The create flow, when a shell hosts the page ---
  const fieldsStep = useFieldsPageStep();
  const setStep = fieldsStep?.setStep;
  // Fields are an inclusion list, so the new one shows by its id before the
  // refetched field list carries it; the reader lands on the roster tab
  // where it now sits.
  const showCreated = useCallback(
    (customFieldId: string) => {
      setFieldShown(customFieldId, true);
      setStep?.({ kind: "browse", tab: "existing" });
    },
    [setFieldShown, setStep],
  );

  // Reorder only meaningful on the unfiltered Shown list (indices match).
  // A mouse drags after 8px of travel, a finger after a hold.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );
  const reorderable = !q;
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      // Sortable rows only — the fixed footer resident (age) never enters
      // itemOrder (its seat isn't a position).
      const keys = shownItems.filter((i) => i.key !== "age").map((i) => i.key);
      const oldIndex = keys.indexOf(String(active.id));
      const newIndex = keys.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      // Persist the FULL shown order — hidden items re-enter at the end
      // when toggled back on (they're absent from the list).
      onChange({ itemOrder: arrayMove(keys, oldIndex, newIndex) });
    },
    [onChange, shownItems],
  );

  if (fieldsStep?.step.kind === "create") {
    return (
      <CreateFieldPage
        type={fieldsStep.step.type}
        onCancel={() => fieldsStep.setStep({ kind: "browse", tab: "create" })}
        onCreated={showCreated}
      />
    );
  }

  const shownMatches = visibleShown.length > 0 || nameMatches;
  const rosterMatches =
    shownMatches ||
    visibleHiddenProps.length > 0 ||
    visibleHiddenFields.length > 0;

  return (
    <FieldsPicker
      query={query}
      onQueryChange={setQuery}
      initialTab={
        fieldsStep?.step.kind === "browse" ? fieldsStep.step.tab : undefined
      }
      rosterMatches={rosterMatches}
      onCreateType={(type) => fieldsStep?.setStep({ kind: "create", type })}
    >
      <>
        {/* Shown — properties + fields combined, in card render order. A
            query that leaves it empty drops the group; the picker owns the
            one empty state. */}
        {shownMatches ? (
        <Box
          direction="column"
          paddingX="md"
          paddingY="sm"
          gap="2xs"
          role="list"
          aria-label="Shown"
        >
          <SectionHeader
            label="Shown"
            count={visibleShown.length + (nameMatches ? 1 : 0)}
          />
          {/* Every card has a title — pinned, not hideable, not reorderable. */}
          {nameMatches ? <PinnedRow label="Project Name" icon="type" /> : null}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableShown.map((i) => i.key)}
              strategy={verticalListSortingStrategy}
            >
              {sortableShown.map((item) => (
                <SortableItemRow
                  key={item.key}
                  item={item}
                  sortable={reorderable}
                  onToggle={handleToggle}
                />
              ))}
            </SortableContext>
          </DndContext>
          {/* Fixed footer resident: toggleable, never draggable — plain
              icon, no grip swap. Last, mirroring the card. */}
          {fixedShown.map((item) => (
            <ItemRow key={item.key} item={item} onToggle={handleToggle} />
          ))}
        </Box>
        ) : null}

        {/* Hidden properties */}
        {visibleHiddenProps.length > 0 ? (
          <>
            {shownMatches ? <Separator /> : null}
            <Box
              direction="column"
              paddingX="md"
              paddingY="sm"
              gap="2xs"
              role="list"
              aria-label="Properties"
            >
              <SectionHeader
                label="Properties"
                count={visibleHiddenProps.length}
              />
              {visibleHiddenProps.map((item) => (
                <ItemRow key={item.key} item={item} onToggle={handleToggle} />
              ))}
            </Box>
          </>
        ) : null}

        {/* Hidden custom fields */}
        {visibleHiddenFields.length > 0 ? (
          <>
            {shownMatches || visibleHiddenProps.length > 0 ? (
              <Separator />
            ) : null}
            <Box
              direction="column"
              paddingX="md"
              paddingY="sm"
              gap="2xs"
              role="list"
              aria-label="Custom Fields"
            >
              <SectionHeader
                label="Custom Fields"
                count={visibleHiddenFields.length}
              />
              {visibleHiddenFields.map((item) => (
                <ItemRow key={item.key} item={item} onToggle={handleToggle} />
              ))}
            </Box>
          </>
        ) : null}
      </>
    </FieldsPicker>
  );
}
